import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import Header from '@/components/Header';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import Footer from '@/components/Footer';
import { 
  Building, 
  MapPin, 
  Calendar, 
  Users, 
  Settings,
  Plus,
  Edit,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';

interface VenueData {
  venue_id: string;
  venue_name: string;
  address_line: string;
  city: string;
  capacity?: number;
  is_active: boolean;
  created_at: string;
}

interface VenueStats {
  totalVenues: number;
  activeGames: number;
  pendingBookings: number;
  monthlyRevenue: number;
}

export default function VenueManagement() {
  const [venues, setVenues] = useState<VenueData[]>([]);
  const [stats, setStats] = useState<VenueStats>({
    totalVenues: 0,
    activeGames: 0,
    pendingBookings: 0,
    monthlyRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    if (user?.id) {
      loadVenueData();
    }
  }, [user]);

  const loadVenueData = async () => {
    try {
      if (!user?.id) return;

      // Get venues owned by this user - filter by claimed_by OR venue_id = user.id (for internal users who have venue sections)
      const { data: venueData, error: venueError } = await supabase
        .from('venues')
        .select('*')
        .or(`claimed_by.eq.${user.id},venue_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (venueError) throw venueError;

      const formattedVenues = (venueData || []).map(venue => ({
        venue_id: venue.venue_id,
        venue_name: venue.venue_name,
        address_line: venue.address_line || venue.venue_address,
        city: venue.city,
        capacity: venue.capacity,
        is_active: venue.is_active,
        created_at: venue.created_at
      }));
      
      setVenues(formattedVenues);

      // Get venue stats for user's venues only
      const { count: venueCount } = await supabase
        .from('venues')
        .select('*', { count: 'exact', head: true })
        .or(`claimed_by.eq.${user.id},venue_id.eq.${user.id}`);

      // Get games for user's venues only
      const { count: gameCount } = await supabase
        .from('venue_games')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'approved')
        .eq('venue_id', user.id)
        .gte('game_date', new Date().toISOString().split('T')[0]);

      // Get pending bookings for user's venues only
      const userVenueIds = formattedVenues.map(v => v.venue_id);
      const { count: pendingBookingsCount } = await supabase
        .from('venue_bookings')
        .select('*', { count: 'exact', head: true })
        .in('venue_id', userVenueIds.length > 0 ? userVenueIds : ['none'])
        .eq('status', 'pending');

      // Calculate monthly revenue for user's venues
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      
      const { data: monthlyBookings } = await supabase
        .from('venue_bookings')
        .select('*')
        .in('venue_id', userVenueIds.length > 0 ? userVenueIds : ['none'])
        .eq('status', 'confirmed')
        .gte('created_at', startOfMonth.toISOString());

      // Get venue rates for revenue calculation
      const { data: venueRates } = await supabase
        .from('venue_rates')
        .select('*')
        .in('venue_id', userVenueIds.length > 0 ? userVenueIds : ['none']);

      let calculatedRevenue = 0;
      if (monthlyBookings && venueRates && venueRates.length > 0) {
        calculatedRevenue = monthlyBookings.reduce((total, booking) => {
          const rate = venueRates.find(r => r.venue_id === booking.venue_id) || venueRates[0];
          const hours = (new Date(`2000-01-01 ${booking.end_time}`).getTime() - 
                        new Date(`2000-01-01 ${booking.start_time}`).getTime()) / (1000 * 60 * 60);
          return total + (hours * parseFloat(rate.hourly_rate.toString()));
        }, 0);
      } else {
        // Fallback: estimate from games and events
        const { data: monthlyGames } = await supabase
          .from('venue_games')
          .select('booking_duration_hours')
          .in('venue_id', userVenueIds.length > 0 ? userVenueIds : ['none'])
          .eq('status', 'approved')
          .gte('created_at', startOfMonth.toISOString());
        
        const { data: monthlyEvents } = await supabase
          .from('venue_events')
          .select('booking_duration_hours')
          .in('venue_id', userVenueIds.length > 0 ? userVenueIds : ['none'])
          .eq('status', 'approved')
          .gte('created_at', startOfMonth.toISOString());
        
        const gameHours = monthlyGames?.reduce((total, game) => total + (game.booking_duration_hours || 2), 0) || 0;
        const eventHours = monthlyEvents?.reduce((total, event) => total + (event.booking_duration_hours || 3), 0) || 0;
        calculatedRevenue = (gameHours + eventHours) * 50; // $50/hour estimate
      }

      setStats({
        totalVenues: venueCount || 0,
        activeGames: gameCount || 0,
        pendingBookings: pendingBookingsCount || 0,
        monthlyRevenue: Math.round(calculatedRevenue)
      });
    } catch (error) {
      console.error('Error loading venue data:', error);
      toast.error('Failed to load venue data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'pending': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'inactive': return <XCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <MobileHeader />
        <div className="container mx-auto px-4 py-8 pt-20">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">Please log in to access venue management</p>
            </CardContent>
          </Card>
        </div>
        <MobileBottomNav />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <MobileHeader />
      
      <div className="container mx-auto px-4 py-8 pt-20 pb-24 md:pb-8">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Venue Management</h1>
            <p className="text-muted-foreground">
              Manage your venues, bookings, and facilities
            </p>
          </div>

          {/* Venue Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Building className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.totalVenues}</p>
                    <p className="text-sm text-muted-foreground">Total Venues</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Calendar className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.activeGames}</p>
                    <p className="text-sm text-muted-foreground">Active Games</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Clock className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{loading ? '-' : stats.pendingBookings}</p>
                    <p className="text-sm text-muted-foreground">Pending Bookings</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <Users className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">${loading ? '-' : stats.monthlyRevenue.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Venue Management Tabs */}
          <Tabs defaultValue="venues" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="venues">My Venues</TabsTrigger>
              <TabsTrigger value="bookings">Bookings</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="venues" className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Venue List</h3>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Venue
                </Button>
              </div>

              {venues.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Venues Found</h3>
                    <p className="text-muted-foreground">
                      Add your first venue to start managing bookings
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {venues.map((venue) => (
                    <Card key={venue.venue_id}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Building className="h-10 w-10 text-primary" />
                            <div>
                              <h4 className="font-semibold">{venue.venue_name}</h4>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-4 w-4" />
                                  {venue.address_line}, {venue.city}
                                </div>
                                {venue.capacity && (
                                  <div className="flex items-center gap-1">
                                    <Users className="h-4 w-4" />
                                    Capacity: {venue.capacity}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={venue.is_active ? 'default' : 'secondary'}>
                              <span className="flex items-center gap-1">
                                {venue.is_active ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                {venue.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </Badge>
                            <Button size="sm" variant="outline">
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="bookings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Booking Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <Button>View All Bookings</Button>
                      <Button variant="outline">Pending Requests</Button>
                      <Button variant="outline">Booking Calendar</Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Booking management features are available in demo mode.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Venue Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Utilization Rate</h4>
                        <p className="text-2xl font-bold text-primary">78%</p>
                        <p className="text-sm text-muted-foreground">This month</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <h4 className="font-semibold mb-2">Average Booking Duration</h4>
                        <p className="text-2xl font-bold text-primary">2.5hrs</p>
                        <p className="text-sm text-muted-foreground">Per session</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Detailed analytics and reporting coming soon.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Venue Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium">Default Booking Duration</label>
                        <Input placeholder="2 hours" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Advance Booking Days</label>
                        <Input placeholder="30 days" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Booking Policies</label>
                      <Textarea 
                        placeholder="Enter your venue booking policies..."
                        rows={4}
                      />
                    </div>
                    <Button>Save Settings</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <MobileBottomNav />
      <Footer />
    </div>
  );
}